import { useEffect, useRef, type KeyboardEvent } from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';

import { useT } from '@/i18n/locale';
import { cn } from '@/lib/utils';
import { pressable } from '@/lib/motion';

export interface StarsProps {
  /** 1–5, or null/undefined when unrated. */
  value: number | null | undefined;
  /** Omit for a read-only render. Tapping the current rating clears it. */
  onRate?: (value: number | null) => void;
  /** `lg` (28px) is the hero's "Rate it" and the rating prompt. */
  size?: 'sm' | 'md' | 'lg';
  'aria-label'?: string;
  /**
   * Arrows select the star they land on (default). `false`: arrows move focus
   * only and Enter/Space/click/1–5 commit — for a control where choosing
   * closes it (the rating prompt), the StatusSheet's APG variant.
   */
  selectOnMove?: boolean;
  /** Focus the group's tab stop on mount (the rating prompt). */
  autoFocus?: boolean;
}

const STAR_VALUES = [1, 2, 3, 4, 5];

/**
 * The five-star rating — `rating` colour only, never a generic highlight
 * (ui-ux.md §2). Interactive when `onRate` is given, read-only otherwise.
 * The interactive group is one tab stop (roving tabindex, ui-ux.md §8).
 */
export function Stars({
  value,
  onRate,
  size = 'md',
  'aria-label': ariaLabel,
  selectOnMove = true,
  autoFocus = false,
}: StarsProps) {
  const t = useT();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const rating = value ?? 0;
  const iconSize = size === 'sm' ? 16 : size === 'lg' ? 28 : 22;
  const anchorIndex = rating >= 1 ? rating - 1 : 0;

  useEffect(() => {
    if (autoFocus) refs.current[anchorIndex]?.focus();
    // Mount only: re-focusing on every rating change would steal focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);
  const label =
    ariaLabel ?? (rating > 0 ? t('component.rating.rated', { value: rating }) : t('component.rating.unrated'));

  if (!onRate) {
    return (
      <div role="img" aria-label={label} className="flex items-center gap-0.5">
        {STAR_VALUES.map((i) => (
          <Star
            key={i}
            aria-hidden
            size={iconSize}
            strokeWidth={1.75}
            className={i <= rating ? 'fill-rating text-rating' : 'text-ink-tertiary'}
          />
        ))}
      </div>
    );
  }

  // Roving tabindex (ui-ux.md §8, the calendar day-grid's pattern): the whole
  // group is one tab stop anchored at the checked star — the first when
  // unrated. Arrows move focus and select the star they land on; Home/End jump
  // to the ends; movement clamps (no wrap), same as the reference. Pointer
  // clicks keep the tap-the-current-star-to-clear toggle. 1–5 pick a star
  // directly.
  const moveTo = (from: number, delta: number) => {
    const next = from + delta;
    if (next < 0 || next >= STAR_VALUES.length) return;
    if (selectOnMove) onRate(STAR_VALUES[next]);
    // The five buttons persist across renders, so focus can move synchronously.
    refs.current[next]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        moveTo(index, -1);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        moveTo(index, 1);
        break;
      case 'Home':
        moveTo(index, -index);
        break;
      case 'End':
        moveTo(index, STAR_VALUES.length - 1 - index);
        break;
      case '1':
      case '2':
      case '3':
      case '4':
      case '5': {
        const picked = Number(event.key);
        onRate(picked);
        refs.current[picked - 1]?.focus();
        break;
      }
      default:
        return;
    }
    event.preventDefault();
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      onClick={(e) => e.stopPropagation()}
      className="flex items-center"
    >
      {STAR_VALUES.map((i, index) => (
        <motion.button
          key={i}
          ref={(el) => {
            refs.current[index] = el;
          }}
          type="button"
          role="radio"
          aria-checked={i === rating}
          aria-label={t('component.rating.rateStar', { value: i })}
          tabIndex={index === anchorIndex ? 0 : -1}
          onClick={() => onRate(i === rating ? null : i)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          {...pressable()}
          className={cn('rounded-sm p-1', size === 'sm' ? '-m-0.5' : size === 'lg' ? '' : '-m-1')}
        >
          <Star
            aria-hidden
            size={iconSize}
            strokeWidth={1.75}
            className={i <= rating ? 'fill-rating text-rating' : 'text-ink-tertiary'}
          />
        </motion.button>
      ))}
    </div>
  );
}
